import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { ScrollReveal } from '@/components/base/ScrollReveal';
import { storePartners } from '@/mocks/faq';

const storesPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '清文華泉合作購書通路',
  description:
    '查找清文華泉 Cinva 出版書籍的合作購書通路。不同書籍在各紙本與數位平台的上架狀態可能不同，實際商品資訊請以通路頁面為準。',
  url: 'https://cinva.sonbook.net/stores',
};

export default function Stores() {
  useEffect(() => {
    document.title = '清文華泉合作通路｜紙本書與電子書購買平台';
  }, []);

  return (
    <>
      <JsonLd data={storesPageSchema} />
      <Navbar />
      <main className="bg-gradient-to-b from-background-50 via-primary-50/30 to-secondary-50/40">

        {/* Intro + Store Grid */}
        <ScrollReveal direction="up">
          <section className="w-full pt-28 pb-14 md:pt-36 md:pb-16">
            <div className="w-full px-6 md:px-10 max-w-[1200px] mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-300 rounded-full mb-5">
                  購書通路
                </span>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                  清文華泉合作購書通路
                </h1>
                <p className="text-sm md:text-base text-foreground-700 max-w-2xl mx-auto leading-relaxed mb-3">
                  清文華泉 Cinva 的合作通路頁提供已建立的紙本書、電子書及數位閱讀平台連結。各書籍在不同平台的上架狀態、版本、價格與庫存可能不同，正式書目請以崧博出版書籍資料庫為準，購買資訊則以各通路頁面為準。
                </p>
                <p className="text-sm md:text-base text-foreground-700 max-w-2xl mx-auto leading-relaxed">
                  本頁整理清文華泉目前已建立的合作購書通路，讀者可依各平台提供的紙本書、電子書或其他閱讀形式查找相關作品。不同書籍的實際上架狀態可能不同，請以各通路正式頁面為準。
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
                  <a
                    href="https://catalog.sonbook.net/"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    前往崧博出版書籍資料庫
                    <i className="ri-external-link-line"></i>
                  </a>
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-foreground-300/60 text-foreground-700 rounded-full text-sm font-medium hover:bg-foreground-950/5 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    認識清文華泉
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>

              {/* Store Grid */}
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3 text-center">
                  選擇適合的購書平台
                </h2>
                <p className="text-sm text-foreground-600 max-w-2xl mx-auto text-center leading-relaxed mb-8">
                  請由下方已建立的正式通路連結前往各平台查找清文華泉書籍。各平台的商品名稱、版本、價格、庫存、優惠與可購買地區可能不同，實際資訊以通路頁面為準。
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                  {storePartners.map((store) => (
                    <a
                      key={store.name}
                      href={store.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="group flex flex-col items-center text-center p-5 md:p-6 rounded-xl bg-background-50 border border-primary-200/40 hover:bg-primary-50 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-50 border border-primary-200/50 mb-3 group-hover:border-primary-400/50 group-hover:bg-primary-100 transition-colors duration-300">
                        <i className="ri-store-2-line text-xl text-primary-500 group-hover:text-primary-600 transition-colors duration-300"></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors duration-200 whitespace-nowrap">
                        {store.name}
                      </span>
                      <span className="text-[11px] text-foreground-500 mt-1 line-clamp-2 leading-relaxed">
                        {store.description}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-[1200px] mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* Purchase Reminder */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-[1200px] mx-auto">
              <div className="max-w-2xl mx-auto">
                <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-200 rounded-full mb-4">
                  購書提醒
                </span>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  購書前請確認平台資訊
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  同一本書在不同通路可能提供不同版本、格式或銷售條件。購買前請確認書名、作者、出版社、ISBN、紙本或電子書格式、適用地區及平台使用規則。
                </p>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-[1200px] mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* Book Catalog CTA */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-[1200px] mx-auto">
              <div className="max-w-2xl mx-auto text-center">
                <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-300 rounded-full mb-5">
                  書目查詢
                </span>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  先查詢正式書目資訊
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                  若需要確認完整書名、作者、ISBN、出版日期與其他正式出版資訊，請前往崧博出版書籍資料庫查詢，再由本頁選擇合適的購書通路。
                </p>
                <a
                  href="https://catalog.sonbook.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  前往崧博出版書籍資料庫
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-[1200px] mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* Brand Cooperation */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16 pb-20 md:pb-28">
            <div className="w-full px-6 md:px-10 max-w-[1200px] mx-auto">
              <div className="max-w-2xl mx-auto">
                <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-300 rounded-full mb-4">
                  合作洽詢
                </span>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  品牌經銷與合作洽詢
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                  如有清文華泉品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過官方聯絡方式提出詢問。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  品牌經銷與合作方案詳情請直接聯繫洽詢。
                </p>
              </div>
            </div>
          </section>
        </ScrollReveal>

      </main>
      <Footer />
    </>
  );
}